import { useState, useCallback } from 'react';

/**
 * usePersistedColumns — persists column visibility to localStorage.
 *
 * Usage:
 *   const [visibleColumns, handleColumnToggle] = usePersistedColumns('campuses', DEFAULT_VISIBLE, ALL_KEYS);
 *
 * @param {string}   pageKey         Unique page identifier (e.g. 'campuses', 'residents')
 * @param {string[]} defaultVisible  Columns visible when no preference is stored
 * @param {string[]} allColumnKeys   Every valid column key — used to filter out stale/invalid keys
 * @returns {[string[], (key: string) => void]}
 */
const STORAGE_PREFIX = 'palcare:columns:';

function readStored(pageKey, allColumnKeys, defaultVisible) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + pageKey);
    if (!raw) return defaultVisible;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultVisible;
    // Filter out any keys that no longer exist in the column defs
    const valid = parsed.filter((k) => allColumnKeys.includes(k));
    return valid.length > 0 ? valid : defaultVisible;
  } catch {
    return defaultVisible;
  }
}

export default function usePersistedColumns(pageKey, defaultVisible, allColumnKeys) {
  const [visibleColumns, setVisibleColumns] = useState(
    () => readStored(pageKey, allColumnKeys, defaultVisible),
  );

  const handleColumnToggle = useCallback((columnKey) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      try {
        localStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(next));
      } catch { /* storage full — degrade silently */ }
      return next;
    });
  }, [pageKey]);

  return [visibleColumns, handleColumnToggle];
}
